import { CpsGridView, EditingViewState } from '@cs-cms/types';

export interface EditingDataValidation {
  hasIncompleteFields: boolean;
  hasIncompleteGlobalFilters: boolean;
  hasIncompleteData: boolean;
}

export interface ViewButtonStates {
  isPrivateViewLimitReached: boolean;
  isGlobalFiltersLimitExceeded: boolean;
  hasIncompleteData: boolean;
  isSaveDisabled: boolean;
  isRenameDisabled: boolean;
  isDeleteDisabled: boolean;
}

export interface ComputeViewButtonStatesParams {
  views: CpsGridView[];
  selectedView: number | null | undefined;
  selectedViewData: CpsGridView | undefined;
  editingViewState: EditingViewState;
  hasUnsavedChanges: boolean;
  privateViewsLimit: number | undefined;
  globalFiltersLimit: number | undefined;
  isGlobalFiltersLimitLoading: boolean;
  hasWritePermission: boolean | undefined;
  isWritePermissionLoading: boolean;
}

const isFieldIncomplete = (field: { id: number; name: string }): boolean => field.id < 0 || !field.name;

const isGlobalFilterIncomplete = (field: { id: number; name: string }): boolean =>
  field.id < 0 || !field.name || field.name === '';

export const getEditingDataValidation = (editingViewState: EditingViewState): EditingDataValidation => {
  const hasIncompleteFields = editingViewState.fields.some(isFieldIncomplete);
  const hasIncompleteGlobalFilters = editingViewState.globalFilters.some(isGlobalFilterIncomplete);

  return {
    hasIncompleteFields,
    hasIncompleteGlobalFilters,
    hasIncompleteData: hasIncompleteFields || hasIncompleteGlobalFilters
  };
};

const countPrivateViews = (views: CpsGridView[]): number => views.filter(view => view.isPrivate).length;

const isPrivateViewLimitReached = (views: CpsGridView[], privateViewsLimit: number | undefined): boolean => {
  const privateViewsCount = countPrivateViews(views);
  return privateViewsLimit === undefined || privateViewsCount >= privateViewsLimit;
};

const isGlobalFiltersLimitExceeded = (
  editingViewState: EditingViewState,
  globalFiltersLimit: number | undefined,
  isGlobalFiltersLimitLoading: boolean
): boolean => {
  if (isGlobalFiltersLimitLoading) {
    return false;
  }

  if (globalFiltersLimit === undefined) {
    return false;
  }

  return editingViewState.globalFilters.length > globalFiltersLimit;
};

const canSaveView = (
  selectedViewData: CpsGridView | undefined,
  editingViewState: EditingViewState,
  hasWritePermission: boolean | undefined = undefined,
  isWritePermissionLoading: boolean
): boolean => {
  const isPrivateView = selectedViewData?.isPrivate ?? false;
  const hasAnyFields = editingViewState.fields.length > 0;
  const hasWriteAccess = !isWritePermissionLoading && hasWritePermission === true;

  return isPrivateView && hasAnyFields && (isPrivateView || hasWriteAccess);
};

const isPrivateViewSelected = (views: CpsGridView[], selectedView: number | null | undefined): boolean => {
  if (selectedView == null) {
    return false;
  }

  const selectedViewEntry = views.find(view => view.id === selectedView);
  return selectedViewEntry?.isPrivate ?? false;
};

const isPrivateViewActionDisabled = (
  views: CpsGridView[],
  selectedView: number | null | undefined
): boolean => {
  if (!selectedView) {
    return true;
  }

  return !isPrivateViewSelected(views, selectedView);
};

export const computeViewButtonStates = (params: ComputeViewButtonStatesParams): ViewButtonStates => {
  const validation = getEditingDataValidation(params.editingViewState);
  const globalFiltersLimitExceeded = isGlobalFiltersLimitExceeded(
    params.editingViewState,
    params.globalFiltersLimit,
    params.isGlobalFiltersLimitLoading
  );
  const saveAllowed = canSaveView(
    params.selectedViewData,
    params.editingViewState,
    params.hasWritePermission,
    params.isWritePermissionLoading
  );

  const saveDisabled =
    validation.hasIncompleteFields ||
    validation.hasIncompleteGlobalFilters ||
    !params.hasUnsavedChanges ||
    !saveAllowed ||
    globalFiltersLimitExceeded;

  return {
    isPrivateViewLimitReached: isPrivateViewLimitReached(params.views, params.privateViewsLimit),
    isGlobalFiltersLimitExceeded: globalFiltersLimitExceeded,
    hasIncompleteData: validation.hasIncompleteData,
    isSaveDisabled: saveDisabled,
    isRenameDisabled: isPrivateViewActionDisabled(params.views, params.selectedView),
    isDeleteDisabled: isPrivateViewActionDisabled(params.views, params.selectedView)
  };
};
